import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Building, Handshake } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * LogoCarousel - A clean, modern horizontal carousel for client/partner logos
 * Features:
 * - Responsive: 5 items desktop, 3 tablet, 1-2 mobile
 * - Smooth sliding animation
 * - Left/right navigation arrows
 * - Touch/swipe support on mobile
 * - Optional auto-scroll
 */
export const LogoCarousel = ({
  items = [],
  language = 'en',
  type = 'client', // 'client' or 'partner'
  autoScroll = false,
  autoScrollInterval = 4000,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);
  const autoScrollRef = useRef(null);

  // Responsive items per view
  const [itemsPerView, setItemsPerView] = useState(5);

  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setItemsPerView(1);
      } else if (width < 640) {
        setItemsPerView(2);
      } else if (width < 768) {
        setItemsPerView(3);
      } else if (width < 1024) {
        setItemsPerView(4);
      } else {
        setItemsPerView(5);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const totalItems = items.length;
  const maxIndex = Math.max(0, totalItems - itemsPerView);

  const goToNext = useCallback(() => {
    if (isAnimating || currentIndex >= maxIndex) return;
    setIsAnimating(true);
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
    setTimeout(() => setIsAnimating(false), 300);
  }, [currentIndex, maxIndex, isAnimating]);

  const goToPrev = useCallback(() => {
    if (isAnimating || currentIndex <= 0) return;
    setIsAnimating(true);
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    setTimeout(() => setIsAnimating(false), 300);
  }, [currentIndex, isAnimating]);

  // Auto-scroll functionality
  useEffect(() => {
    if (!autoScroll || totalItems <= itemsPerView) return;

    autoScrollRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= maxIndex) return 0;
        return prev + 1;
      });
    }, autoScrollInterval);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [autoScroll, autoScrollInterval, maxIndex, itemsPerView, totalItems]);

  // Touch handlers for swipe support
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) < minSwipeDistance) return;

    if (distance > 0) {
      goToNext();
    } else {
      goToPrev();
    }
  };

  // Pause auto-scroll on hover
  const handleMouseEnter = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoScroll && totalItems > itemsPerView) {
      autoScrollRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= maxIndex) return 0;
          return prev + 1;
        });
      }, autoScrollInterval);
    }
  };

  if (totalItems === 0) return null;

  const showNavigation = totalItems > itemsPerView;
  const itemWidth = 100 / itemsPerView;
  const translateX = currentIndex * itemWidth;

  const FallbackIcon = type === 'partner' ? Handshake : Building;

  return (
    <div 
      className={cn("relative w-full", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Navigation Arrows */}
      {showNavigation && (
        <>
          {/* Left Arrow */}
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 z-10",
              "w-10 h-10 md:w-12 md:h-12 rounded-full",
              "bg-white shadow-lg border border-gray-100",
              "flex items-center justify-center",
              "transition-all duration-200",
              "hover:bg-[#33AADD] hover:text-white hover:border-[#33AADD]",
              "disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-400 disabled:hover:border-gray-100",
              "-translate-x-1/2 md:-translate-x-4"
            )}
            aria-label="Previous"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={goToNext}
            disabled={currentIndex >= maxIndex}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 z-10",
              "w-10 h-10 md:w-12 md:h-12 rounded-full",
              "bg-white shadow-lg border border-gray-100",
              "flex items-center justify-center",
              "transition-all duration-200",
              "hover:bg-[#33AADD] hover:text-white hover:border-[#33AADD]",
              "disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-400 disabled:hover:border-gray-100",
              "translate-x-1/2 md:translate-x-4"
            )}
            aria-label="Next"
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </>
      )}

      {/* Carousel Container */}
      <div 
        ref={containerRef}
        className="overflow-hidden mx-6 md:mx-16"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${translateX}%)` }}
        >
          {items.map((item, index) => {
            const itemId = type === 'partner' ? item.partner_id : item.client_id;
            const itemName = language === 'ar' && item.name_ar ? item.name_ar : item.name;
            
            return (
              <div
                key={itemId}
                className="flex-shrink-0 px-2 md:px-3"
                style={{ width: `${itemWidth}%` }}
                data-testid={`${type}-carousel-item-${index}`}
              >
                <div className="bg-white rounded-xl p-4 md:p-6 h-24 md:h-28 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 border border-gray-50">
                  {item.logo_url ? (
                    <a
                      href={item.website_url || '#'}
                      target={item.website_url ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      className="w-full h-full flex items-center justify-center group"
                      title={itemName}
                    >
                      <img
                        src={item.logo_url}
                        alt={item.name}
                        className="max-h-full max-w-full object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-1">
                      <FallbackIcon className="h-8 w-8 text-gray-300" />
                      <span className="text-xs md:text-sm font-medium text-[#6B7280] text-center line-clamp-2">
                        {itemName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dot Indicators (for mobile) */}
      {showNavigation && totalItems <= 10 && (
        <div className="flex justify-center mt-4 gap-1.5 md:hidden">
          {Array.from({ length: Math.ceil(totalItems / itemsPerView) }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrentIndex(Math.min(idx * itemsPerView, maxIndex));
              }}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                Math.floor(currentIndex / itemsPerView) === idx
                  ? "bg-[#33AADD] w-4"
                  : "bg-gray-300 hover:bg-gray-400"
              )}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LogoCarousel;
